import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { prefersNoAutoplay } from '../../util/prefersNoAutoplay';

import css from './ListingCard.module.css';

// Re-export for existing callers/tests.
export { prefersNoAutoplay };

// A card must hold still in view this long before we spend bytes on it. Without the dwell
// gate, one fast scroll down a 40-card search page fetches all 40 clips.
const DWELL_BEFORE_LOAD_MS = 400;

/**
 * Inline looping video for a listing card.
 *
 * The `src` is attached only after the card has dwelled in view, and the HTML `autoplay`
 * attribute is deliberately not used: JS controls playback so off-screen and scrolled-past
 * cards never fetch or decode. Looping itself is free — the file downloads once and replays
 * from buffer — so the metered quantity is one fetch per browser per clip.
 *
 * Playback is started from a React effect after `src` commits — calling `play()` in the same
 * tick as `setIsSourceAttached(true)` races the DOM update and can leave the poster stuck.
 */
const ListingCardVideo = props => {
  const { className, video, title } = props;
  const videoRef = useRef(null);
  const dwellTimerRef = useRef(null);
  const [isSourceAttached, setIsSourceAttached] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const element = videoRef.current;
    if (!element || typeof IntersectionObserver === 'undefined' || prefersNoAutoplay()) {
      // No observer (SSR, older browsers) or an explicit user preference: poster only.
      return undefined;
    }

    const clearDwellTimer = () => {
      if (dwellTimerRef.current) {
        clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            clearDwellTimer();
            setIsIntersecting(false);
            element.pause();
            return;
          }
          setIsIntersecting(true);
          clearDwellTimer();
          dwellTimerRef.current = setTimeout(() => {
            // Attach only — play() runs in a later effect once React has committed `src`.
            setIsSourceAttached(true);
          }, DWELL_BEFORE_LOAD_MS);
        });
      },
      { rootMargin: '0px', threshold: 0.5 }
    );

    observer.observe(element);
    return () => {
      clearDwellTimer();
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isSourceAttached || !isIntersecting) {
      return undefined;
    }
    const element = videoRef.current;
    if (!element) {
      return undefined;
    }
    const playPromise = element.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
    return undefined;
  }, [isSourceAttached, isIntersecting]);

  return (
    <div className={classNames(css.cardVideoWrapper, className)}>
      <video
        ref={videoRef}
        className={css.cardVideo}
        src={isSourceAttached ? video.cardMp4Url : undefined}
        loop
        muted
        playsInline
        preload="none"
        aria-label={title}
        onPlaying={() => setIsPlaying(true)}
      />
      {video.cardPosterUrl ? (
        <img
          className={classNames(css.cardVideoPoster, {
            [css.cardVideoPosterHidden]: isPlaying,
          })}
          src={video.cardPosterUrl}
          alt={title}
          loading="lazy"
        />
      ) : null}
    </div>
  );
};

export default ListingCardVideo;
